// Story 7.6 — the post-login home feed at /feed. "The Wire".
//
// Renders the Story 7.5 GET /api/v1/feed cascade through the full card
// taxonomy (features/feed/FeedCard.tsx dispatcher) with cursor-based
// infinite scroll. Rendered inside <AppShell> (App.tsx layout route), so
// this owns only its content column — the persistent nav/chrome +
// balance pill live in the shell.
//
// Epic 8 seam option B: the page ground is warm sand (bg-brand-paper-200
// — not white, but out of the green family so it can't blend with the
// forest column); The
// Wire is a framed DARK forest column inset on it — a deliberate dark
// artifact with a gold
// hairline, xl radius, and its own mono "The Wire · live" label. Matches
// the "objects on paper" metaphor and costs no rework on the shell.
//
// Marketplace-not-SoMe posture: no sidebar, no stories reel, no trending
// column, no "who to follow". Single centered column, generous rhythm.

import { Link } from "react-router-dom";

import { FeedCard } from "../features/feed/FeedCard";
import { useFeed } from "../features/feed/useFeed";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";

export function FeedPage() {
  const feed = useFeed();

  const sentinelRef = useIntersectionObserver<HTMLDivElement>(
    () => {
      if (feed.hasNextPage && !feed.isFetchingNextPage) {
        void feed.fetchNextPage();
      }
    },
    // Trigger line sits 200px below the viewport bottom (AC 6).
    { rootMargin: "0px 0px 200px 0px", enabled: feed.hasNextPage },
  );

  return (
    <div className="bg-brand-paper-200 text-brand-ink-900">
      <main className="mx-auto flex min-h-[calc(100vh-3.5rem)] w-full max-w-2xl flex-col px-6 py-8">
        {/* Seam option B — the framed dark wire column on the light paper
            page. The column's own "The Wire · live" mono label is the only
            heading; the page ground is warm sand (bg-brand-paper-200) —
            off-white per the design review, but warm rather than green so
            the dark forest column reads as a distinct object on paper.
            flex-1 so the forest column fills the available height (a tall
            framed panel) — the content flows top→"caught up", green below. */}
        <section
          className="flex flex-1 flex-col rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 p-5 text-brand-paper-100 shadow-md"
          aria-label="The Wire"
        >
          <p className="m-0 mb-5 font-mono text-[0.62rem] uppercase tracking-[0.1em] text-brand-gold-300">
            The Wire · live
          </p>

          {feed.isPending ? (
            <div className="flex justify-center py-12" aria-busy="true">
              <Spinner label="Loading feed" />
            </div>
          ) : feed.isError ? (
            <FeedNotice
              testId="feed-error"
              title="We couldn't load your feed"
              body="Something went wrong fetching the latest posts. Try refreshing in a moment."
            />
          ) : feed.items.length > 0 ? (
            <>
              <ul className="flex list-none flex-col gap-5 p-0">
                {feed.items.map((card) => (
                  <li key={card.id}>
                    <FeedCard card={card} />
                  </li>
                ))}
              </ul>

              {feed.isFetchingNextPage ? (
                <div className="flex justify-center py-4" aria-busy="true">
                  <Spinner label="Loading more" />
                </div>
              ) : feed.hasNextPage ? (
                <div
                  ref={sentinelRef}
                  className="h-4"
                  data-testid="feed-sentinel"
                />
              ) : (
                <p
                  className="m-0 pt-5 text-center font-body text-sm text-brand-paper-100/50"
                  data-testid="feed-caught-up"
                >
                  You&apos;re all caught up.
                </p>
              )}
            </>
          ) : (
            <FeedEmpty />
          )}
        </section>
      </main>
    </div>
  );
}

/**
 * The Wire silence state — the dashed ghost card (Epic 8 feed template),
 * deliberately NOT parchment: a quiet dashed panel in Greenhold's
 * no-noise voice with a single ghost link out to a dossier.
 */
function FeedEmpty() {
  return (
    <div
      className="rounded-lg border border-dashed border-brand-gold-500/30 bg-brand-forest-900/40 px-6 py-12 text-center"
      data-testid="feed-empty"
    >
      <p className="m-0 font-heading text-base italic text-brand-paper-100/80">
        The Wire is quiet right now.
      </p>
      <p className="m-0 mx-auto mt-2 max-w-sm font-body text-sm italic text-brand-paper-100/60">
        Greenhold doesn&apos;t manufacture noise — the first creators are being
        onboarded. Editorial and creator posts will surface here as they land.
      </p>
      <p className="m-0 mt-4">
        <Link
          to="/shop"
          className="font-body text-sm not-italic text-brand-gold-300 hover:brightness-110"
        >
          Open a dossier →
        </Link>
      </p>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div
      className="h-8 w-8 animate-spin rounded-full border-2 border-brand-gold-300 border-t-transparent"
      aria-label={label}
    />
  );
}

function FeedNotice({
  testId,
  title,
  body,
}: {
  testId: string;
  title: string;
  body: string;
}) {
  return (
    <div className="px-6 py-8 text-center" data-testid={testId}>
      <h2 className="m-0 font-heading text-xl text-brand-gold-300">{title}</h2>
      <p className="m-0 mx-auto mt-2 max-w-sm font-body text-sm text-brand-paper-100/70">
        {body}
      </p>
    </div>
  );
}
